import cx from 'classnames'
import React from 'react'

import Layout from '@cinch/oxd-layouts/regular/src'

import Author from '@cinch/oxd-components/author/src'
import Image from '@cinch/oxd-components/image/src'
import JargonCard from '@cinch/oxd-components/jargon-card/src'
import Panel from '@cinch/oxd-components/panel/src'
import RichText from '@cinch/oxd-components/rich-text/src/rich-text.component'
import tokens from '@cinch/oxd-utils/tokens/src'

import styles from './article.module.css'

interface Author {
  givenName: string
  familyName: string
  bio?: string
  occupation?: string
  photo: {
    url: string
  }
}

interface Jargon {
  question: string
  summary: string
  uid: string
  id: string
}

export interface ArticlePageProps {
  title: string
  subTitle?: string
  featuredImage: any
  leadParagraph?: string
  quote?: string
  body: any
  author?: Author
  relatedJargon: Jargon[]
  createdAt: string
}

const ArticlePage: React.FC<ArticlePageProps> = (props) => {
  const { title, subTitle, featuredImage, body, leadParagraph, quote, author, relatedJargon, createdAt } = props

  const dateMatch = createdAt.match(/.*(?=\+)/) // fix for IE not understanding `+0000` at end of date str
  const articlePublishedDate = !!dateMatch && !!dateMatch.length ? new Date(dateMatch[0]) : null
  const publishedOn = articlePublishedDate
    ? articlePublishedDate
        .toLocaleDateString('en-GB', {
          hour12: true,
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
        .toUpperCase()
    : undefined

  const hasRelatedJargon = !!relatedJargon.length

  return (
    <Layout isSlim={true}>
      <article>
        <header className={styles.header}>
          <h1 className={styles.title}>{title}</h1>
          {subTitle && <p className={styles.subTitle}>{subTitle}</p>}
          {!!author && (
            <Author
              imgSrc={author.photo}
              givenName={author.givenName}
              familyName={author.familyName}
              occupation={publishedOn}
            />
          )}
          {featuredImage && featuredImage.l ? (
            <picture>
              <source srcSet={featuredImage.l.url} media={tokens.customMedia.s} />
              <source srcSet={featuredImage.m.url} media={tokens.customMedia.xs} />
              <source srcSet={featuredImage.s.url} media={tokens.customMedia.xxs} />
              <img className={styles.featuredImage} alt={featuredImage.alt} src={featuredImage.s.url} />
            </picture>
          ) : (
            <Image className={styles.featuredImage} alt={featuredImage.alt} src={featuredImage.url} />
          )}
        </header>
        <div className={styles.content}>
          <div className={cx(styles.main, { [styles.mainNoPadding]: !hasRelatedJargon })}>
            <RichText>{leadParagraph}</RichText>
            {quote && <blockquote className={styles.quote}>{quote}</blockquote>}
            <RichText>{body}</RichText>
          </div>
          {hasRelatedJargon && (
            <aside className={styles.jargonBuster}>
              {relatedJargon.map(({ id, uid, ...jargon }) => (
                <JargonCard key={id} uid={uid} {...jargon} />
              ))}
            </aside>
          )}
        </div>

        {!!author && (
          <>
            <p className={styles.authorHeading}>About the author</p>
            <Panel className={styles.authorPanel}>
              <Author
                imgSrc={author.photo}
                givenName={author.givenName}
                familyName={author.familyName}
                bio={author.bio}
                occupation={author.occupation}
              />
            </Panel>
          </>
        )}
      </article>
    </Layout>
  )
}

export default ArticlePage
